import { useTranslation } from 'react-i18next';

import { HumanResourcesItem } from '@/app/models/detailsRH';
import { useHumanResourcesExpenseQuery, useUpdateHumanResourcesExpenseMutation } from '@/app/services/detailsHRApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import useFormattedCpfAndCpnj from '@/hooks/useFormattedCpfAndCpnj';
import { Box, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import LabeledValue from './LabeledValue';

interface NewEditValuesHumanResourcesProps {
  selectedItem: HumanResourcesItem | null;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewEditValuesHumanResources(props: NewEditValuesHumanResourcesProps) {
  const { t } = useTranslation(['humanResource', 'common']);
  const { selectedItem, setDrawerOpen } = props;
  const theme = useTheme();

  const [expenses, setExpenses] = useState<{ id: number; groupId: number; finalValue: number }[]>([]);
  const { value: cpf } = useFormattedCpfAndCpnj(selectedItem?.cpf);

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const { data } = useHumanResourcesExpenseQuery(
    {
      id: selectedItem?.id as number,
      bringTotalized: false,
    },
    {
      skip: !selectedItem,
    },
  );

  const [update] = useUpdateHumanResourcesExpenseMutation();

  const handleSubmit = async () => {
    setDrawerOpen(false);
    await toast.promise(
      update({
        detailId: data?.detailId as number,
        expenses: expenses.map((e) => ({
          groupId: Number(e.groupId),
          finalValue: e.finalValue,
          id: e.id,
        })),
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('hr.updated.successfully') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 30px)',
        maxHeight: 'calc(100vh - 30px)',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('edit.values')} />
        <Box
          sx={{
            display: 'block',
            flexDirection: 'row',
            mt: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'block', minWidth: theme.spacing(31), mb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('common:collaborator')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={selectedItem?.name}
                  disabled
                />
              </Stack>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" sx={{ width: '100%', display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="fieldHeader" sx={{ width: theme.spacing(12), mb: 0 }}>
                  {t('cpf')}
                </Typography>
                <TextField
                  sx={{
                    flexGrow: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  size="small"
                  value={cpf}
                  disabled
                />
              </Stack>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />

        {data?.expenseList?.map((expense) => (
          <LabeledValue
            key={data.detailId}
            selectedItem={expense}
            onValueChange={(id, groupId, finalValue) => {
              setExpenses((prev) => {
                const exists = prev.find((e) => e.id === id);
                if (exists) {
                  return prev.map((e) => (e.id === id ? { ...e, finalValue } : e));
                }
                return [...prev, { id, groupId, finalValue }];
              });
            }}
          />
        ))}
      </Box>
      <Box sx={{ mt: 'auto', mb: 4 }}>
        <DrawerButtons
          onSave={() => {
            handleSubmit();
          }}
          onCancel={handleCancel}
          labelSave={t('common:save') as string}
        />
      </Box>
    </Box>
  );
}
